import {
    Form,
    FormField,
    FormSection,
    FormType,
    FilteredValue,
    InputType,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { VentasEnvironmentDefinition } from '../environment-variables';
import { VentasListsDefinition } from './list-variables';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { SectorModel } from 'src/app/models/form-common/sector.model';
import { VentaModel } from 'src/app/models/ventas/venta.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { Compradores } from 'src/app/models/ventas/compradores.model';
import { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';
import { list } from '@fitogest/reactive-form-builder/utils';
import { getDropdownSelectedArray, getDropdownSelectedSingle } from '@fitogest/reactive-form-builder/utils';

export class VentasFormDefinition {
	public formType!: FormType;

	public env: VentasEnvironmentDefinition = new VentasEnvironmentDefinition();

	public lists: VentasListsDefinition = new VentasListsDefinition();

	public usuario = StorageManager.getUser();

	public cliente = StorageManager.getClient();

	public model: ReactiveFormValue<VentaModel> = {} as ReactiveFormValue<VentaModel>;

	public tag: ReactiveFormChangedField<VentaModel> = '';

	public formFieldsSection!: FormSection<object>;

	public formFields!: Form<VentaModel>;

	public fieldNames: FormField<any>[] = [];

	public fieldLabels: string[] = [];

	constructor(type: FormType, _model: Record<string, string>, table: (string[] | string[][])[] | null) {
		this.formType = +type;

		const action = this.formType === FormType.CREATION ? 'Crear' : 'Actualizar';

		this.formFields = {
			name: 'ventas',
			header: action + ' ' + this.env.ventaName,
			type: this.formType,
			sections: this.getFormSections(),
			table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
			uploadButton: {
				isEnabled: false,
				label: '',
			},
			signed: false,
			previousDataTransform: (previousData) => this.normalizePreviousData(previousData),
			dropdownCatalogs: {
				critical: ['id_finca', 'id_sector', 'ids_parcelas', 'id_variedad', 'id_comprador'],
				deferred: ['id_cliente', 'clientes_comercios', 'cooperativas'],
			},
		};
	}

	public getFormFields(): FormField<any>[] {
		const formFields: FormField<any>[] = [
			{
				name: this.formType < 3 ? 'id_finca' : 'finca',
				label: 'Finca',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: this.lists.fincas,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<FincasModel>): boolean => {
							const storedUser = StorageManager.getUser();
							const storedClient = StorageManager.getClient();
							const fincaClientId = this.parseNullableNumber(it.value?.id_cliente);

							if (!this.env.hasClients || !this.env.userCanSeeClientes) {
								if (storedUser.tipo === 'comunero') {
									const userClientId = this.parseNullableNumber(storedUser.id_cliente);
									const effectiveClientId = it.value?.nombre === 'PREFACIT' ? 9 : fincaClientId;

									return effectiveClientId === userClientId;
								}

								return true;
							}

							if (storedClient !== null) {
								const clientId = this.parseNullableNumber(storedClient.id);

								return fincaClientId === clientId;
							}

							return true;
						},
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 1,
			},
			{
				name: this.formType < 3 ? 'id_sector' : 'sector',
				label: this.env.sectorName,
				input: {
					type: InputType.MULTISELECT_SEARCH,
					isEditable: true,
					data: this.lists.sectores,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<SectorModel>) =>
							this.sameDropdownId(it.value?.id_finca, getDropdownSelectedSingle(this.lists.fincas)?.id),
					},
				},
				dependencies: [
					{ source: 'id_finca', target: 'id_sector', kind: 'filter', legacy: true },
				],
				dependencyBehaviour: {
					clearOnParentChange: true,
					keepCurrentValueIfStillAvailable: true,
					keepValueUntilOptionsLoaded: true,
					preservePanelOnReload: true,
					preserveFilterOnReload: true,
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 2,
			},
			{
				name: this.formType < 3 ? 'ids_parcelas' : 'parcela',
				label: this.env.parcelasName,
				input: {
					type: InputType.MULTISELECT_SEARCH,
					isEditable: true,
					data: this.lists.parcelas,
					dropdownConfiguration: {
						summation: {
							summand: 'superficie_cultivada',
							name: this.env.tipoSuperficieParcelas,
							uds: this.env.textoSuperficie,
							format: (total: number) =>
								total.toLocaleString('es-ES', {
									useGrouping: true,
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								}),
						},
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<ParcelasModel>) => {
							if (it.value?.activo?.toString() !== '1') {
								return false;
							}

							const sectoresSeleccionados = getDropdownSelectedArray(this.lists.sectores);
							const fincaSeleccionada = getDropdownSelectedSingle(this.lists.fincas);

							if (sectoresSeleccionados.length > 0) {
								return sectoresSeleccionados.some((sector: SectorModel) =>
									this.sameDropdownId(sector?.id, it.value?.id_sector));
							}

							return this.sameDropdownId(it.value.id_finca, fincaSeleccionada?.id);
						},
					},
				},
				dependencies: [
					{ source: 'id_finca', target: 'ids_parcelas', kind: 'filter', legacy: true },
					{ source: 'id_sector', target: 'ids_parcelas', kind: 'filter', legacy: true },
				],
				dependencyBehaviour: {
					clearOnParentChange: true,
					keepCurrentValueIfStillAvailable: true,
					keepValueUntilOptionsLoaded: true,
					preservePanelOnReload: true,
					preserveFilterOnReload: true,
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: !['pyf', 'avafitogest'].includes(this.env.appName),
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 3,
			},
			{
				name: 'fecha',
				label: 'Fecha',
				input: { type: InputType.CALENDAR, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 4,
			},
			{
				name: 'albaran',
				label: 'Nº Albarán / Factura',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 5,
			},
			{
				name: 'lote',
				label: 'Nº Lote',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.showVentasLote,
				},
				order: 6,
			},
			{
				name: this.formType < 3 ? 'id_comprador' : 'comprador',
				label: this.env.appName === 'esparragosgranada' ? 'Cooperativa' : 'Comercio',
				input: {
					type: InputType.DROPDOWN_SEARCH,
					isEditable: true,
					data: this.lists.compradores,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<Compradores>) => it.value?.tipo === 'Comercio',
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible:
						this.env.showVentasComercio && this.env.appName !== 'esparragosgranada' && this.env.appName !== 'covidai',
				},
				order: 7,
			},
			{
				name: this.formType < 3 ? 'id_comprador' : 'comprador',
				label: 'Clientes',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: this.lists.clientes,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'covidai',
				},
				order: 8,
			},
			{
				name: this.formType < 3 ? 'id_comprador' : 'comprador',
				label: this.env.appName === 'esparragosgranada' ? 'Cooperativa' : 'Comercio',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: this.lists.compradores,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<Compradores>) => it.value?.tipo === 'Comercio',
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.showVentasComercio && this.env.appName === 'esparragosgranada',
				},
				order: 9,
			},
			{
				name: 'producto',
				label: 'Producto',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'pyf',
				},
				order: 10,
			},
			{
				name: this.formType < 3 ? 'id_variedad' : 'variedad',
				label: 'Variedad',
				input: {
					type: InputType.MULTISELECT_SEARCH,
					isEditable: true,
					data: this.lists.variedades,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<VisitasVariedadMesModel>) => {
							if (this.isSinVariedadOption(it.value)) {
								return true;
							}

							const seleccionadas = getDropdownSelectedArray(this.lists.parcelas);
							if (!seleccionadas.length) return false;

							if (this.env.activateSIEX && !this.env.hasPlantas) {
								return seleccionadas.some((parcela) => this.sameDropdownId(parcela.id_variedad, it.value?.id));
							}

							return seleccionadas.some((parcela) => this.sameDropdownId(parcela.id_variedad_legacy, it.value?.id));
						},
					},
				},
				dependencies: [
					{ source: 'ids_parcelas', target: 'id_variedad', kind: 'filter', legacy: true },
				],
				dependencyBehaviour: {
					clearOnParentChange: false,
					keepCurrentValueIfStillAvailable: true,
					keepValueUntilOptionsLoaded: true,
					preservePanelOnReload: true,
					preserveFilterOnReload: true,
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName !== 'pyf',
				},
				order: 11,
			},
			{
				name: 'cantidad',
				label: 'Cantidad Kg',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 12,
			},
			{
				name: 'precio',
				label: 'Precio',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 13,
			},
			{
				name: 'precio_kg',
				label:
					this.env.appName === 'fotoagricultura'
						? 'Precio / Kg'
						: this.env.appName === 'agrosol'
							? 'Importe Parcial'
							: 'Importe',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 14,
			},
			{
				name: 'descuento_porcentaje',
				label: 'Descuento %',
				input: { type: InputType.NUMERIC, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'agrosol',
				},
				order: 15,
			},
			{
				name: 'descuento_euros',
				label: 'Descuento €',
				input: { type: InputType.NUMERIC, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'agrosol',
				},
				order: 16,
			},
			{
				name: 'importe_total',
				label: 'Importe Total',
				input: { type: InputType.NUMERIC, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'agrosol',
				},
				order: 17,
			},
			{
				name: 'matricula',
				label: 'Matricula',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'campanaragricola',
				},
				order: 17,
			},
			{
				name: 'estado',
				label: 'Estado',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: list(
						['...', null],
						['Esperando disponibilidad', 'Esperando disponibilidad'],
						['Esperando otro movimiento', 'Esperando otro movimiento'],
						['Reservado', 'Reservado'],
						['Parcialmente disponible', 'Parcialmente disponible'],
					),
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'momparler',
				},
				order: 17,
			},
		];

		this.fieldNames = this.fieldNames.concat(formFields.filter((it) => it.conditions.isVisible));
		this.fieldLabels = this.fieldLabels.concat(
			formFields.filter((it) => it.conditions.isVisible).map((it) => it.label),
		);
		return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}

	public getHiddenFields(): FormField<any>[] {
		const hiddenFields: FormField<any>[] = [
			{
				name: 'id',
				label: '',
				input: {
					type: InputType.HIDDEN,
					defaultValue: this.model.id,
					isEditable: false,
				},
				width: 2,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 1,
			},
			{
				name: 'id_cliente',
				label: '',
				input: {
					type: InputType.HIDDEN,
					defaultValue: this.cliente?.id !== null ? this.cliente?.id : getDropdownSelectedSingle(this.lists.fincas)?.id_cliente,
					isEditable: false,
				},
				updateValue: () => (this.cliente?.id !== null ? this.cliente?.id : getDropdownSelectedSingle(this.lists.fincas)?.id_cliente),
				width: 2,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.hasClients,
				},
				order: 2,
			},
			{
				name: 'id_usuario',
				label: '',
				input: {
					isEditable: false,
					defaultValue:
						this.env.applicationType === 'cropgest'
							? (this.cliente?.id_usuario ?? this.usuario.id)
							: (this.cliente?.id_usuario_asignado ?? this.model.id_usuario ?? this.usuario.id),
					type: InputType.HIDDEN,
				},
				updateValue: () =>
					this.env.applicationType === 'cropgest'
						? (this.cliente?.id_usuario ?? this.usuario.id)
						: (this.cliente?.id_usuario_asignado ?? this.model.id_usuario ?? this.usuario.id),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 3,
			},
			{
                name: 'cultivo',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                updateValue: () => this.resolveVentaCultivo(),
                order: 4
            },
            {
                name: 'variedad',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                updateValue: () => this.resolveVentaVariedad(),
                order: 5
            },
		];

		this.fieldNames = this.fieldNames.concat(hiddenFields.filter((it) => it.conditions.isVisible));
		this.fieldLabels = this.fieldLabels.concat(
			hiddenFields.filter((it) => it.conditions.isVisible).map((it) => it.label),
		);
		return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}

	private getFormSections(): FormSection<any>[] {
		const sections: FormSection<any>[] = [
			{
				name: '',
				width: 12,
				isVisible: true,
				fields: this.getFormFields(),
				order: 1,
			},
			{
				name: '',
				width: 12,
				isVisible: true,
				fields: this.getHiddenFields(),
				order: 2,
			},
		];

		return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}

	private normalizePreviousData(previousData: VentaModel): VentaModel {
		const normalizedData: VentaModel = { ...previousData };
		const writableData = normalizedData as Record<string, unknown>;

		if (this.formType < 3) {
			writableData['id_sector'] = this.normalizeMultiDropdownValue(normalizedData.id_sector);
			writableData['ids_parcelas'] = this.normalizeMultiDropdownValue(normalizedData.ids_parcelas);
			writableData['id_variedad'] = this.normalizeMultiDropdownValue(normalizedData.id_variedad);
		}

		return normalizedData;
	}

	private normalizeMultiDropdownValue(value: unknown): string[] | null {
		if (Array.isArray(value)) {
			const values = value
				.filter((item) => this.hasDropdownValue(item))
				.map((item) => String(item).trim());

			return values.length > 0 ? values : null;
		}

		if (!this.hasDropdownValue(value)) {
			return null;
		}

		const values = String(value)
			.split(/[;,]/)
			.map((item) => item.trim())
			.filter((item) => this.hasDropdownValue(item));

		return values.length > 0 ? values : null;
	}

	private resolveVentaCultivo(): string {
		const selectedVariedades = this.getSelectedVentaVariedades();
		const cultivoValues = this.collectVentaValues(selectedVariedades, 'cultivo', 'cultivo_legacy');

		if (cultivoValues.length > 0) {
			return this.joinVentaListValues(cultivoValues);
		}

		return this.resolveSelectedParcelasValue('cultivo', 'cultivo_legacy');
	}

	private resolveVentaVariedad(): string {
		const selectedVariedades = this.getSelectedVentaVariedades();
		const variedadValues = this.collectVentaValues(selectedVariedades, 'variedad', 'variedad_legacy');

		if (variedadValues.length > 0) {
			return this.joinVentaListValues(variedadValues);
		}

		return this.resolveSelectedParcelasValue('variedad', 'variedad_legacy');
	}

	private getSelectedVentaVariedades(): VisitasVariedadMesModel[] {
		const selectedVariedades = getDropdownSelectedArray(this.lists.variedades) as VisitasVariedadMesModel[];
		const validSelectedVariedades = selectedVariedades.filter((value) => this.isObjectValue(value));

		if (validSelectedVariedades.length > 0) {
			return validSelectedVariedades;
		}

		const selectedVariedad = getDropdownSelectedSingle(this.lists.variedades) as VisitasVariedadMesModel | null;

		return this.isObjectValue(selectedVariedad) ? [selectedVariedad] : [];
	}

	private resolveSelectedParcelasValue(primaryKey: keyof ParcelasModel, legacyKey: keyof ParcelasModel): string {
		const selectedParcelas = getDropdownSelectedArray(this.lists.parcelas) as ParcelasModel[];
		const values = this.collectVentaValues(selectedParcelas, primaryKey, legacyKey);

		return this.joinVentaListValues(values);
	}

	private collectVentaValues<T extends Record<string, unknown>>(items: T[], primaryKey: keyof T, legacyKey: keyof T): string[] {
		return items
			.map((item) => item[primaryKey] ?? item[legacyKey] ?? '')
			.map((value) => String(value).trim())
			.filter((value) => this.hasDropdownValue(value));
	}

	private joinVentaListValues(values: string[]): string {
		const uniqueValues = values.filter((value, index, allValues) => allValues.indexOf(value) === index);

		return uniqueValues.join(';');
	}

	private isObjectValue<T extends object>(value: T | null | undefined): value is T {
		return value !== null && value !== undefined && typeof value === 'object';
	}

	private isSinVariedadOption(value: VisitasVariedadMesModel | null | undefined): boolean {
		const record = value as Record<string, unknown> | null | undefined;
		const normalizedValue = String(record?.['variedad'] ?? record?.['nombre'] ?? '').trim().toLowerCase();

		return normalizedValue === 'sin variedad' || normalizedValue === 'sin-variedad' || normalizedValue === 'sin_variedad';
	}

	private sameDropdownId(firstValue: unknown, secondValue: unknown): boolean {
		if (!this.hasDropdownValue(firstValue) || !this.hasDropdownValue(secondValue)) {
			return false;
		}

		return String(firstValue) === String(secondValue);
	}

	private hasDropdownValue(value: unknown): boolean {
		if (value === null || value === undefined) {
			return false;
		}

		if (typeof value === 'string') {
			const normalizedValue = value.trim().toLowerCase();

			return normalizedValue !== '' && normalizedValue !== 'null' && normalizedValue !== 'undefined';
		}

		return true;
	}

	private parseNullableNumber(value: unknown): number | null {
		if (typeof value === 'number' && Number.isFinite(value)) {
			return value;
		}

		if (typeof value === 'string' && value.trim() !== '') {
			const parsedValue = Number(value);

			return Number.isFinite(parsedValue) ? parsedValue : null;
		}

		return null;
	}
}
